function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useMemo, useRef } from 'react';
import { useStore } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { isBankAccountConnected } from "../../../store/store.model";
import { useConnectBankAccount } from "../../../hooks/useConnectBankAccount";
import ExternalLinkButton from "../../../components/ExternalLinkButton";
import { helpArticles } from "../../../utils/helpArticles";
import { useStartKYC } from "../../../hooks/useStartKYCPersona";
import { openInNewTabProps } from "../../../constants";
import styles from "./StepsData.module.scss";
import { Button, Typography } from 'seagull';
import { KYBProgressStatusEnum, KYBProgressStatusHelpers } from 'anchor-kyb-service/types';
import { useUploadLogo } from "../../../hooks/useUploadLogo";
import { LogoPicker } from "../../../components/LogoPicker/LogoPicker";
import { CreateProposalButton } from "../../../components/CreateProposalButton/CreateProposalButton";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function useSetupSteps() {
  const {
    logBI
  } = useStore(s => s.actions);
  const worksAt = useStore(selectors.user.business);
  const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
  const hasDisabledPayments = useStore(selectors.user.hasDisabledPayments);
  const personaKYBStatus = useStore(s => {
    var _s$personaKYBStatus$d;
    return (_s$personaKYBStatus$d = s.personaKYBStatus.data) === null || _s$personaKYBStatus$d === void 0 ? void 0 : _s$personaKYBStatus$d.status;
  });
  const isKycStepComplete = personaKYBStatus && KYBProgressStatusHelpers.isApproved(personaKYBStatus);
  const {
    Description,
    ConnectButton,
    BankAccountDetails
  } = useConnectBankAccount();
  const {
    startKYC
  } = useStartKYC();
  const {
    uploadLogo
  } = useUploadLogo(worksAt);
  const logoInputRef = useRef(null);
  const logoUrl = useStore(selectors.user.businessLogo);
  const numSample = useStore(selectors.user.numSampleAgreements);
  const totalSampleAgreements = Object.values(numSample !== null && numSample !== void 0 ? numSample : {}).reduce((acc, curr) => acc + curr, 0);
  const isConnectBankAccountStepComplete = useStore(selectors.user.hasBankAccountConnected);
  const handleLogoButtonClick = useCallback(() => {
    var _logoInputRef$current;
    (_logoInputRef$current = logoInputRef.current) === null || _logoInputRef$current === void 0 || _logoInputRef$current.click();
  }, []);
  const logoUpdated = useCallback(function () {
    logBI(11203);
    uploadLogo(...arguments);
  }, [logBI, uploadLogo]);
  const uploadYourLogoStep = useMemo(() => ({
    title: 'Upload your logo',
    status: logoUrl ? 'completed' : 'initial',
    content: /*#__PURE__*/_jsxs("div", {
      className: styles.logoStepContainer,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.logoStepContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Your logo will appear on your proposals, agreements and invoices."
        }), /*#__PURE__*/_jsx(Typography, {
          children: "Please upload a PNG file under 5MB."
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.actionButton,
          children: [/*#__PURE__*/_jsx("div", {
            className: styles.logoPicker,
            children: /*#__PURE__*/_jsx(LogoPicker, {
              inputRef: logoInputRef,
              onChange: logoUpdated,
              image: logoUrl || ''
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleLogoButtonClick,
            children: logoUrl ? 'Change file' : 'Upload file'
          })]
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: styles.logoPreview,
        children: logoUrl && /*#__PURE__*/_jsx("img", {
          alt: "logo",
          src: logoUrl
        })
      })]
    })
  }), [logoUrl, logoUpdated, handleLogoButtonClick]);
  const sendAProposalStep = useMemo(() => {
    const isComplete = totalSampleAgreements > 0;
    return {
      title: 'Send a proposal',
      status: isComplete ? 'completed' : 'initial',
      content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Get a feel of the Anchor experience by creating a proposal and sending it to a client (or even yourself!)"
        }), /*#__PURE__*/_jsx("div", {
          className: styles.actionButton,
          children: /*#__PURE__*/_jsx(CreateProposalButton, {
            hideTemplatePicker: true,
            text: isComplete ? 'Create another proposal' : 'Create proposal'
          })
        })]
      })
    };
  }, [totalSampleAgreements]);
  const connectYourBankStep = useMemo(() => ({
    title: 'Connect your bank account',
    status: isConnectBankAccountStepComplete ? 'completed' : 'initial',
    content: isConnectBankAccountStepComplete ? isBankAccountConnected(payoutBankAccount) && /*#__PURE__*/_jsx(BankAccountDetails, {
      payoutBankAccount: payoutBankAccount
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Description, {
        linkTestid: "onboarding-widget-connect-account-read-more"
      }), /*#__PURE__*/_jsx(ConnectButton, {
        className: styles.actionButton,
        testid: "onboarding-widget-connect-account-btn",
        enterManuallyTestid: "onboarding-widget-routing-number"
      })]
    })
  }), [isConnectBankAccountStepComplete, payoutBankAccount, Description, ConnectButton, BankAccountDetails]);
  const verifyYourBusinessStep = useMemo(() => {
    const title = 'Verify your business';
    const subtextInfo = /*#__PURE__*/_jsxs(Typography, {
      variant: "paragraph2",
      className: styles.kybNote,
      children: ['Verification is typically completed within a few minutes to a few business days, and may take longer if additional information is required. ', /*#__PURE__*/_jsx(ExternalLinkButton, {
        variant: "paragraph2",
        openInNewTab: true,
        underline: "none",
        href: helpArticles.kybProcessOverview(),
        children: "Read more"
      })]
    });
    if (isKycStepComplete) {
      return {
        title,
        status: 'completed',
        content: /*#__PURE__*/_jsx(Typography, {
          children: "You've successfully completed the KYB process."
        })
      };
    }
    if (personaKYBStatus && KYBProgressStatusHelpers.isAwaitingDecision(personaKYBStatus) || personaKYBStatus === KYBProgressStatusEnum.Errored) {
      return {
        title,
        status: 'pending',
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Typography, {
            children: "Your KYB information has been submitted and is now under review. We\u2019ll let you know if any additional details are required."
          }), subtextInfo]
        })
      };
    }
    return {
      title,
      status: 'initial',
      content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Anchor is legally required to verify your business and its associated individuals through a Know Your Business (KYB) verification. Completing this step is necessary to receive payments on Anchor."
        }), subtextInfo, /*#__PURE__*/_jsx("div", {
          className: styles.actionButton,
          children: /*#__PURE__*/_jsx(Button, {
            "data-testid": "onboarding-widget-complete-kyc",
            onClick: startKYC,
            children: "Complete KYB"
          })
        })]
      })
    };
  }, [isKycStepComplete, personaKYBStatus, startKYC]);
  const activateYourAccountStep = useMemo(() => {
    const disabled = !isKycStepComplete || !isConnectBankAccountStepComplete;
    return {
      title: 'Activate your account',
      status: hasDisabledPayments ? 'initial' : 'completed',
      content: hasDisabledPayments ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Identity verification is required to process payments through Anchor. Schedule a quick 5-minute video call below and have your picture ID ready."
        }), /*#__PURE__*/_jsx("div", {
          className: styles.actionButton,
          children: /*#__PURE__*/_jsx(Button, _objectSpread(_objectSpread({
            size: "medium",
            "data-testid": "activate-your-account-button",
            as: "a",
            href: "https://book.avoma.com/anchor/enable-payments/"
          }, openInNewTabProps), {}, {
            tooltip: disabled ? {
              title: /*#__PURE__*/_jsx("div", {
                children: "To verify your identity, ensure your bank account is connected and your business verification is complete."
              })
            } : undefined,
            disabled: disabled,
            children: "Verify identity"
          }))
        })]
      }) : /*#__PURE__*/_jsx(Typography, {
        children: "You've successfully activated your account."
      })
    };
  }, [hasDisabledPayments, isConnectBankAccountStepComplete, isKycStepComplete]);
  return useMemo(() => [uploadYourLogoStep, sendAProposalStep, connectYourBankStep, verifyYourBusinessStep, activateYourAccountStep], [uploadYourLogoStep, sendAProposalStep, connectYourBankStep, verifyYourBusinessStep, activateYourAccountStep]);
}